import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../i18n';
import { TechDiagram } from '../components/TechDiagram';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';

const Step = ({ step, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const left = index % 2 === 0;

  return (
    <div ref={ref} className="relative" data-testid={`process-step-${step.n}`}>
      <motion.span
        animate={
          inView
            ? { backgroundColor: '#d4d8dc', boxShadow: '0 0 14px rgba(212,216,220,0.35)' }
            : { backgroundColor: '#1c1c21', boxShadow: '0 0 0 rgba(0,0,0,0)' }
        }
        transition={{ duration: 0.5 }}
        className="absolute left-4 top-1.5 z-10 h-3 w-3 -translate-x-1/2 rounded-full md:left-1/2"
      />
      <div className={`pl-12 md:w-1/2 md:pl-0 ${left ? 'md:pr-16 md:text-right' : 'md:ml-auto md:pl-16'}`}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-sm text-steel-label">{step.n}</span>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-steel-bright">
            {step.title}
          </h3>
          <p className={`mt-3 max-w-md text-sm leading-relaxed text-steel ${left ? 'md:ml-auto' : ''}`}>
            {step.desc}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const Process = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="process-page" className="pt-16">
      <section className="relative overflow-hidden">
        <TechDiagram
          variant="circuit"
          className="pointer-events-none absolute -left-28 top-20 aspect-square w-[380px] opacity-30"
        />
        <div className="mx-auto max-w-7xl px-6 py-24">
          <PageHero overline={t.process.overline} title={t.process.title} intro={t.process.intro} titleClassName="" />
        </div>
      </section>

      <section className="pb-28" data-testid="process-timeline">
        <div className="relative mx-auto max-w-7xl px-6">
          <span className="absolute bottom-0 left-4 top-0 w-px bg-graphite-200 md:left-1/2" />
          <div className="space-y-16 md:space-y-20">
            {t.process.steps.map((s, i) => (
              <Step key={s.n} step={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Process;
